export type SpecStatus = 'draft' | 'published' | 'archived'

export type SpecSelection = {
  category: string // e.g. 'Exterior', 'Interior'
  subcategory: string // e.g. 'Roofing', 'Kitchen', 'Flooring'
  variantSet: string // e.g. 'Shingles', 'Appliance Package'
  optionId: string // e.g. 'ext-1'
  optionName: string // e.g. 'Black Asphalt Shingles'
  price: number
}

export type StagingStyle = {
  id: string
  name: string
  description: string
  thumbnailUrl: string
}

export const stagingStyles: StagingStyle[] = [
  {
    id: 'modern-farmhouse',
    name: 'Modern Farmhouse',
    description:
      'Warm woods, shiplap accents, and neutral tones with rustic charm',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=400&q=80',
  },
  {
    id: 'mid-century',
    name: 'Mid-Century Modern',
    description:
      'Clean lines, organic curves, and iconic retro-inspired furniture',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?auto=format&fit=crop&w=400&q=80',
  },
  {
    id: 'contemporary',
    name: 'Contemporary',
    description:
      'Sleek minimalism with bold art pieces and monochromatic palettes',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=400&q=80',
  },
  {
    id: 'coastal',
    name: 'Coastal',
    description:
      'Light and airy with ocean-inspired blues, whites, and natural textures',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=400&q=80',
  },
  {
    id: 'traditional',
    name: 'Traditional',
    description:
      'Classic elegance with rich fabrics, dark woods, and timeless details',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=400&q=80',
  },
]

export type RenderStatus = 'ready' | 'rendering' | 'queued' | 'not-generated'

export type MarketingAssets = {
  stillImages: number
  motionAssets: number
  tourUrl: string | null
  generatedAt: string | null
}

export type Spec = {
  id: string
  name: string
  modelId: string
  modelName: string
  description: string
  status: SpecStatus
  selections: SpecSelection[]
  totalUpgradePrice: number
  createdAt: string
  updatedAt: string
  stagingStyleId: string | null
  renderStatus: RenderStatus
  marketingAssets: MarketingAssets
}

export const mockSpecs: Spec[] = [
  {
    id: 'spec-1',
    name: 'Premium Package',
    modelId: 'model-1',
    modelName: 'The Lawson',
    description:
      'High-end finishes including quartz countertops and hardwood flooring.',
    status: 'published',
    totalUpgradePrice: 16700,
    createdAt: '2026-02-10',
    updatedAt: '2026-02-15',
    stagingStyleId: 'contemporary',
    renderStatus: 'ready',
    marketingAssets: {
      stillImages: 8,
      motionAssets: 2,
      tourUrl: 'https://tour.resvr.com/lawson-premium',
      generatedAt: '2026-02-16',
    },
    selections: [
      {
        category: 'Exterior',
        subcategory: 'Roofing',
        variantSet: 'Shingles',
        optionId: 'ext-2',
        optionName: 'Gray Asphalt Shingles',
        price: 1200,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Appliance Package',
        optionId: 'int-1',
        optionName: 'Stainless Steel Appliances',
        price: 2500,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Countertops',
        optionId: 'int-6',
        optionName: 'White Quartz Countertops',
        price: 4500,
      },
      {
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'Hardwood',
        optionId: 'int-8',
        optionName: 'Natural Oak Hardwood',
        price: 8500,
      },
    ],
  },
  {
    id: 'spec-2',
    name: 'Standard Package',
    modelId: 'model-1',
    modelName: 'The Lawson',
    description: 'The base configuration with all standard included options.',
    status: 'published',
    totalUpgradePrice: 0,
    createdAt: '2026-02-12',
    updatedAt: '2026-02-12',
    stagingStyleId: 'modern-farmhouse',
    renderStatus: 'ready',
    marketingAssets: {
      stillImages: 6,
      motionAssets: 1,
      tourUrl: 'https://tour.resvr.com/lawson-standard',
      generatedAt: '2026-02-13',
    },
    selections: [
      {
        category: 'Exterior',
        subcategory: 'Roofing',
        variantSet: 'Shingles',
        optionId: 'ext-1',
        optionName: 'Black Asphalt Shingles',
        price: 0,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Appliance Package',
        optionId: 'int-2',
        optionName: 'Black + White Appliances',
        price: 0,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Countertops',
        optionId: 'int-7',
        optionName: 'Laminate Countertops',
        price: 0,
      },
    ],
  },
  {
    id: 'spec-3',
    name: 'Luxury Collection',
    modelId: 'model-2',
    modelName: 'The Ridgewood',
    description:
      'Top-tier built-in appliances and premium finishes throughout.',
    status: 'published',
    totalUpgradePrice: 22200,
    createdAt: '2026-02-20',
    updatedAt: '2026-02-25',
    stagingStyleId: 'mid-century',
    renderStatus: 'rendering',
    marketingAssets: {
      stillImages: 0,
      motionAssets: 0,
      tourUrl: null,
      generatedAt: null,
    },
    selections: [
      {
        category: 'Exterior',
        subcategory: 'Roofing',
        variantSet: 'Shingles',
        optionId: 'ext-2',
        optionName: 'Gray Asphalt Shingles',
        price: 1200,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Appliance Package',
        optionId: 'int-3',
        optionName: 'Built-in Appliances',
        price: 8500,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Countertops',
        optionId: 'int-6',
        optionName: 'White Quartz Countertops',
        price: 4500,
      },
      {
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'Hardwood',
        optionId: 'int-8',
        optionName: 'Natural Oak Hardwood',
        price: 8500,
      },
    ],
  },
  {
    id: 'spec-4',
    name: 'Modern Minimalist',
    modelId: 'model-2',
    modelName: 'The Ridgewood',
    description: 'Clean lines with stainless steel and quartz.',
    status: 'published',
    totalUpgradePrice: 15500,
    createdAt: '2026-03-01',
    updatedAt: '2026-03-02',
    stagingStyleId: 'contemporary',
    renderStatus: 'queued',
    marketingAssets: {
      stillImages: 0,
      motionAssets: 0,
      tourUrl: null,
      generatedAt: null,
    },
    selections: [
      {
        category: 'Exterior',
        subcategory: 'Roofing',
        variantSet: 'Shingles',
        optionId: 'ext-1',
        optionName: 'Black Asphalt Shingles',
        price: 0,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Appliance Package',
        optionId: 'int-1',
        optionName: 'Stainless Steel Appliances',
        price: 2500,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Countertops',
        optionId: 'int-6',
        optionName: 'White Quartz Countertops',
        price: 4500,
      },
      {
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'Hardwood',
        optionId: 'int-8',
        optionName: 'Natural Oak Hardwood',
        price: 8500,
      },
    ],
  },
  {
    id: 'spec-5',
    name: "Builder's Choice",
    modelId: 'model-3',
    modelName: 'The Birchwood',
    description: 'A balanced mix of standard and upgraded features.',
    status: 'published',
    totalUpgradePrice: 2500,
    createdAt: '2026-03-05',
    updatedAt: '2026-03-05',
    stagingStyleId: 'modern-farmhouse',
    renderStatus: 'ready',
    marketingAssets: {
      stillImages: 5,
      motionAssets: 1,
      tourUrl: 'https://tour.resvr.com/birchwood-builders',
      generatedAt: '2026-03-06',
    },
    selections: [
      {
        category: 'Exterior',
        subcategory: 'Roofing',
        variantSet: 'Shingles',
        optionId: 'ext-1',
        optionName: 'Black Asphalt Shingles',
        price: 0,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Appliance Package',
        optionId: 'int-1',
        optionName: 'Stainless Steel Appliances',
        price: 2500,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Countertops',
        optionId: 'int-7',
        optionName: 'Laminate Countertops',
        price: 0,
      },
    ],
  },
  {
    id: 'spec-6',
    name: 'Upgraded Bungalow',
    modelId: 'model-3',
    modelName: 'The Birchwood',
    description: 'Fully upgraded single-story living.',
    status: 'draft',
    totalUpgradePrice: 16700,
    createdAt: '2026-03-10',
    updatedAt: '2026-03-10',
    stagingStyleId: 'coastal',
    renderStatus: 'not-generated',
    marketingAssets: {
      stillImages: 0,
      motionAssets: 0,
      tourUrl: null,
      generatedAt: null,
    },
    selections: [
      {
        category: 'Exterior',
        subcategory: 'Roofing',
        variantSet: 'Shingles',
        optionId: 'ext-2',
        optionName: 'Gray Asphalt Shingles',
        price: 1200,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Appliance Package',
        optionId: 'int-1',
        optionName: 'Stainless Steel Appliances',
        price: 2500,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Countertops',
        optionId: 'int-6',
        optionName: 'White Quartz Countertops',
        price: 4500,
      },
      {
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'Hardwood',
        optionId: 'int-8',
        optionName: 'Natural Oak Hardwood',
        price: 8500,
      },
    ],
  },
  {
    id: 'spec-7',
    name: 'Estate Showcase',
    modelId: 'model-4',
    modelName: 'The Summit',
    description: 'The ultimate luxury configuration for our estate model.',
    status: 'draft',
    totalUpgradePrice: 22200,
    createdAt: '2026-03-12',
    updatedAt: '2026-03-12',
    stagingStyleId: null,
    renderStatus: 'not-generated',
    marketingAssets: {
      stillImages: 0,
      motionAssets: 0,
      tourUrl: null,
      generatedAt: null,
    },
    selections: [
      {
        category: 'Exterior',
        subcategory: 'Roofing',
        variantSet: 'Shingles',
        optionId: 'ext-2',
        optionName: 'Gray Asphalt Shingles',
        price: 1200,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Appliance Package',
        optionId: 'int-3',
        optionName: 'Built-in Appliances',
        price: 8500,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Countertops',
        optionId: 'int-6',
        optionName: 'White Quartz Countertops',
        price: 4500,
      },
      {
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'Hardwood',
        optionId: 'int-8',
        optionName: 'Natural Oak Hardwood',
        price: 8500,
      },
    ],
  },
  {
    id: 'spec-8',
    name: 'Sales Center Display',
    modelId: 'model-1',
    modelName: 'The Lawson',
    description: 'Configuration matching the physical sales center model.',
    status: 'published',
    totalUpgradePrice: 22200,
    createdAt: '2026-03-14',
    updatedAt: '2026-03-14',
    stagingStyleId: 'traditional',
    renderStatus: 'ready',
    marketingAssets: {
      stillImages: 10,
      motionAssets: 3,
      tourUrl: 'https://tour.resvr.com/lawson-sales-center',
      generatedAt: '2026-03-14',
    },
    selections: [
      {
        category: 'Exterior',
        subcategory: 'Roofing',
        variantSet: 'Shingles',
        optionId: 'ext-2',
        optionName: 'Gray Asphalt Shingles',
        price: 1200,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Appliance Package',
        optionId: 'int-3',
        optionName: 'Built-in Appliances',
        price: 8500,
      },
      {
        category: 'Interior',
        subcategory: 'Kitchen',
        variantSet: 'Countertops',
        optionId: 'int-6',
        optionName: 'White Quartz Countertops',
        price: 4500,
      },
      {
        category: 'Interior',
        subcategory: 'Flooring',
        variantSet: 'Hardwood',
        optionId: 'int-8',
        optionName: 'Natural Oak Hardwood',
        price: 8500,
      },
    ],
  },
]
